import React from 'react';
import { ArrowRight, Sparkles, Zap } from 'lucide-react';
import MagneticButton from './MagneticButton';
import { Button } from './ui/button';

const CTASection = () => {
  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="cta-section">
      {/* Animated Background */}
      <div className="cta-background">
        <div className="cta-gradient cta-gradient-1"></div>
        <div className="cta-gradient cta-gradient-2"></div>
        <div className="cta-gradient cta-gradient-3"></div>
        
        {/* Animated grid */}
        <div className="cta-grid"></div>
        
        {/* Floating particles */}
        <div className="cta-particles">
          {[...Array(20)].map((_, i) => (
            <div key={i} className="cta-particle" style={{
              left: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 5}s`,
              animationDuration: `${3 + Math.random() * 4}s`
            }}></div>
          ))}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="cta-content">
          {/* Icon */}
          <div className="cta-icon-container">
            <div className="cta-icon-ring cta-icon-ring-1"></div>
            <div className="cta-icon-ring cta-icon-ring-2"></div>
            <div className="cta-icon-background">
              <Zap className="w-12 h-12 text-white" />
            </div>
          </div>

          {/* Text */}
          <h2 className="cta-title">
            <span className="cta-title-gradient">
              Projenizi Hayata Geçirmeye Hazır mısınız?
            </span>
          </h2>

          <p className="cta-description">
            Dijital dönüşüm yolculuğunuzda size eşlik edelim. 
            Hemen başlayalım ve fikirlerinizi gerçeğe dönüştürelim.
          </p>

          {/* CTA Buttons */}
          <div className="cta-buttons">
            <MagneticButton>
              <Button
                onClick={scrollToContact}
                size="lg"
                className="cta-button cta-button-primary"
              >
                <Sparkles className="w-5 h-5 mr-2" />
                Ücretsiz Keşif Toplantısı
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </MagneticButton>

            <MagneticButton>
              <Button
                onClick={() => document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' })}
                size="lg"
                variant="outline"
                className="cta-button cta-button-secondary"
              >
                Hizmetlerimizi Keşfedin
              </Button>
            </MagneticButton>
          </div>

          {/* Trust badges */}
          <div className="cta-badges">
            <div className="cta-badge">
              <span className="cta-badge-emoji">✨</span>
              <span className="cta-badge-text">7/24 Destek</span>
            </div>
            <div className="cta-badge">
              <span className="cta-badge-emoji">🚀</span>
              <span className="cta-badge-text">Hızlı Teslimat</span>
            </div>
            <div className="cta-badge">
              <span className="cta-badge-emoji">🏆</span>
              <span className="cta-badge-text">%100 Memnuniyet</span>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .cta-section {
          padding: 8rem 0;
          position: relative;
          overflow: hidden;
          background: #0a0f1a;
        }

        .cta-background {
          position: absolute;
          inset: 0;
          pointer-events: none;
        }

        .cta-gradient {
          position: absolute;
          border-radius: 50%;
          filter: blur(120px);
          animation: pulse-glow 8s ease-in-out infinite;
        }

        .cta-gradient-1 {
          width: 700px;
          height: 700px;
          background: radial-gradient(circle, rgba(6, 182, 212, 0.25) 0%, transparent 70%);
          top: -200px;
          left: 50%;
          transform: translateX(-50%);
        }

        .cta-gradient-2 {
          width: 500px;
          height: 500px;
          background: radial-gradient(circle, rgba(20, 184, 166, 0.2) 0%, transparent 70%);
          bottom: -150px;
          right: -100px;
          animation-delay: 2s;
        }

        .cta-gradient-3 {
          width: 500px;
          height: 500px;
          background: radial-gradient(circle, rgba(16, 185, 129, 0.15) 0%, transparent 70%);
          bottom: -150px;
          left: -100px;
          animation-delay: 4s;
        }

        @keyframes pulse-glow {
          0%, 100% {
            opacity: 0.4;
            transform: scale(1);
          }
          50% {
            opacity: 0.7;
            transform: scale(1.1);
          }
        }

        .cta-grid {
          position: absolute;
          inset: 0;
          background-image: 
            linear-gradient(rgba(6, 182, 212, 0.05) 1px, transparent 1px),
            linear-gradient(90deg, rgba(6, 182, 212, 0.05) 1px, transparent 1px);
          background-size: 50px 50px;
          mask-image: radial-gradient(ellipse at center, black 20%, transparent 80%);
          animation: grid-move 20s linear infinite;
        }

        @keyframes grid-move {
          0% {
            background-position: 0 0;
          }
          100% {
            background-position: 50px 50px;
          }
        }

        .cta-particles {
          position: absolute;
          inset: 0;
        }

        .cta-particle {
          position: absolute;
          width: 4px;
          height: 4px;
          background: linear-gradient(135deg, #06b6d4, #14b8a6);
          border-radius: 50%;
          bottom: -10px;
          animation: particle-rise linear infinite;
          opacity: 0;
        }

        @keyframes particle-rise {
          0% {
            opacity: 0;
            transform: translateY(0) scale(0);
          }
          10% {
            opacity: 1;
            transform: translateY(-100px) scale(1);
          }
          90% {
            opacity: 1;
          }
          100% {
            opacity: 0;
            transform: translateY(-800px) scale(0);
          }
        }

        .cta-content {
          text-align: center;
          position: relative;
          z-index: 1;
        }

        .cta-icon-container {
          position: relative;
          width: 120px;
          height: 120px;
          margin: 0 auto 2rem;
        }

        .cta-icon-ring {
          position: absolute;
          inset: 0;
          border-radius: 50%;
          border: 2px solid transparent;
          border-top-color: rgba(6, 182, 212, 0.6);
          border-right-color: rgba(20, 184, 166, 0.4);
        }

        .cta-icon-ring-1 {
          animation: rotate-ring 4s linear infinite;
        }

        .cta-icon-ring-2 {
          animation: rotate-ring 6s linear infinite reverse;
          transform: scale(0.8);
        }

        @keyframes rotate-ring {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .cta-icon-background {
          position: absolute;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #06b6d4, #14b8a6);
          border-radius: 50%;
          transform: scale(0.5);
          box-shadow: 0 0 60px rgba(6, 182, 212, 0.6);
          animation: icon-pulse 2s ease-in-out infinite;
        }

        @keyframes icon-pulse {
          0%, 100% {
            transform: scale(0.5);
            box-shadow: 0 0 60px rgba(6, 182, 212, 0.6);
          }
          50% {
            transform: scale(0.55);
            box-shadow: 0 0 80px rgba(6, 182, 212, 0.8);
          }
        }

        .cta-title {
          font-size: 3rem;
          font-weight: 800;
          margin-bottom: 1.5rem;
          line-height: 1.2;
        }

        .cta-title-gradient {
          background: linear-gradient(135deg, #06b6d4 0%, #14b8a6 50%, #10b981 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          animation: gradient-shift 3s ease infinite;
          background-size: 200% 200%;
        }

        @keyframes gradient-shift {
          0%, 100% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
        }

        .cta-description {
          font-size: 1.25rem;
          color: #94a3b8;
          max-width: 600px;
          margin: 0 auto 3rem;
          line-height: 1.8;
        }

        .cta-buttons {
          display: flex;
          flex-wrap: wrap;
          gap: 1.5rem;
          justify-content: center;
          margin-bottom: 3rem;
        }

        .cta-button {
          padding: 1.5rem 3rem;
          font-size: 1.125rem;
          font-weight: 600;
          transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
        }

        .cta-button-primary {
          background: linear-gradient(135deg, #06b6d4, #14b8a6);
          color: white;
          box-shadow: 0 10px 40px rgba(6, 182, 212, 0.4);
          position: relative;
          overflow: hidden;
        }

        .cta-button-primary::before {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg, #14b8a6, #10b981);
          opacity: 0;
          transition: opacity 0.4s ease;
        }

        .cta-button-primary:hover::before {
          opacity: 1;
        }

        .cta-button-primary:hover {
          transform: translateY(-4px);
          box-shadow: 0 20px 60px rgba(6, 182, 212, 0.6);
        }

        .cta-button-secondary {
          border: 2px solid rgba(6, 182, 212, 0.5);
          color: #06b6d4;
          background: rgba(6, 182, 212, 0.05);
          backdrop-filter: blur(20px);
        }

        .cta-button-secondary:hover {
          background: rgba(6, 182, 212, 0.15);
          border-color: #06b6d4;
          transform: translateY(-4px);
        }

        .cta-badges {
          display: flex;
          flex-wrap: wrap;
          gap: 2rem;
          justify-content: center;
        }

        .cta-badge {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.75rem 1.5rem;
          background: rgba(6, 182, 212, 0.1);
          border: 1px solid rgba(6, 182, 212, 0.2);
          border-radius: 9999px;
          backdrop-filter: blur(10px);
          transition: all 0.3s ease;
        }

        .cta-badge:hover {
          background: rgba(6, 182, 212, 0.15);
          border-color: rgba(6, 182, 212, 0.4);
          transform: translateY(-2px);
        }

        .cta-badge-emoji {
          font-size: 1.5rem;
        }

        .cta-badge-text {
          color: #e2e8f0;
          font-weight: 600;
        }

        @media (max-width: 768px) {
          .cta-title {
            font-size: 2rem;
          }

          .cta-description {
            font-size: 1rem;
          }

          .cta-buttons {
            flex-direction: column;
            align-items: stretch;
          }
        }
      `}</style>
    </section>
  );
};

export default CTASection;